import { FC, useCallback, useMemo, useState } from "react";
import { Button } from "@src/lib/ui/Button";
import Text from "@src/lib/ui/Text";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@src/lib/ui/Dialog";
import SelectUsersDialog from "./SelectUsersDialog";
import { UserSelectionInput, UserType } from "@src/types.generated";
import { useUpdateAutomationMutation } from "@src/features/contentDetail/premiumModuleDetail/AutoReassignToggle.generated";
import { launchConfetti } from "@src/lib/utils/confetti";
import { useToast } from "@src/lib/hooks/useToast";
import useUserSelectionSetInputState from "./hooks/useUserSelectionSetInputState";
import UserSelectionSetPreview from "./UserSelectionSetPreview";
import { AutomationItem } from "@src/features/sidebars/shared/constants";
import { isUserSelectionInputEmpty } from "@src/features/contentLibrary/utils/assignments";

type Props = {
  /** A line under the rules — what else being in the library means. */
  footnote?: string;
  automationItem: AutomationItem;
  refetchQueries?: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const LibraryAccessAutomationDialog: FC<Props> = ({
  footnote,
  refetchQueries = [],
  open,
  onOpenChange,
  ...props
}) => {
  const [selectUsersDialogOpen, setSelectUsersDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [addDialogKey, setAddDialogKey] = useState(0);

  const { addToast, addErrorToast } = useToast();

  const {
    userSelectionSetInput,
    deleteUserSelection,
    editUserSelection,
    addUserSelection,
  } = useUserSelectionSetInputState({
    initialUserSelectionSetFragment:
      props.automationItem.automation.userSelectionSet,
  });

  const isUserSelectionSetInputEmpty = useMemo(() => {
    if (!userSelectionSetInput) return true;
    if (userSelectionSetInput.userSelections.length === 0) return true;
    return userSelectionSetInput.userSelections.every(
      isUserSelectionInputEmpty,
    );
  }, [userSelectionSetInput]);

  const [updateAccess, { loading }] = useUpdateAutomationMutation({
    refetchQueries,
    onCompleted: (data) => {
      if (data.updateAutomation.success) {
        launchConfetti();
        addToast({
          message: "Library Access updated",
        });
        onOpenChange(false);
      } else {
        addErrorToast({
          ...data,
          callsite: "library_access_automation_dialog",
        });
      }
    },
    onError: () => {
      addErrorToast({
        message: "An unexpected error occurred while updating access",
        callsite: "library_access_automation_dialog",
      });
    },
  });

  const handleAccessChange = useCallback(() => {
    updateAccess({
      variables: {
        id: props.automationItem.automation.id,
        input: { userSelectionSet: userSelectionSetInput },
      },
    });
  }, [updateAccess, props.automationItem.automation.id, userSelectionSetInput]);

  const handleEditUserSelectionSet = useCallback((index: number) => {
    setEditingIndex(index);
    setSelectUsersDialogOpen(true);
  }, []);

  const handleAddUserSelectionSet = useCallback(() => {
    setEditingIndex(null);
    setAddDialogKey((prev) => prev + 1);
    setSelectUsersDialogOpen(true);
  }, []);

  const handleRemoveUserSelectionSet = useCallback(
    (index: number) => {
      deleteUserSelection(index);
    },
    [deleteUserSelection],
  );

  const handleSelectUsersDialogSelect = useCallback(
    (selection: UserSelectionInput) => {
      if (editingIndex !== null) {
        editUserSelection(editingIndex, selection);
      } else {
        addUserSelection(selection);
      }
      setSelectUsersDialogOpen(false);
      setEditingIndex(null);
    },
    [editingIndex, editUserSelection, addUserSelection],
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-[528px] gap-0 overflow-hidden p-0"
          data-testid="library-access-automation-dialog"
        >
          <DialogHeader className="flex h-[60px] flex-row items-center justify-between border-b border-border px-6 py-4">
            <DialogTitle className="text-left">Library Access</DialogTitle>
          </DialogHeader>
          <div className="flex max-h-[80vh] min-h-[240px] flex-col gap-6 overflow-y-auto p-6">
            <div className="shrink-0">
              <UserSelectionSetPreview
                showCounts={false}
                input={userSelectionSetInput}
                showUserTypeOptions={true}
                onEdit={handleEditUserSelectionSet}
                onRemove={handleRemoveUserSelectionSet}
                onAdd={handleAddUserSelectionSet}
              />
            </div>
            {footnote && (
              <Text
                variant="helper-description"
                className="text-text-secondary"
                multiline
              >
                {footnote}
              </Text>
            )}
          </div>
          <DialogFooter className="gap-2 border-t border-border px-6 py-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAccessChange}
              loading={loading}
              disabled={isUserSelectionSetInputEmpty || loading}
              data-testid="save-button"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {selectUsersDialogOpen && (
        <SelectUsersDialog
          key={
            editingIndex !== null
              ? `edit-${editingIndex}`
              : `add-${addDialogKey}`
          }
          open={selectUsersDialogOpen}
          onOpenChange={(open) => {
            setSelectUsersDialogOpen(open);
            if (!open) {
              setEditingIndex(null);
            }
          }}
          input={
            editingIndex !== null
              ? userSelectionSetInput.userSelections[editingIndex]
              : {
                  allLocations: false,
                  allManagers: false,
                  allTrainees: false,
                  allAdmins: false,
                }
          }
          onSelect={handleSelectUsersDialogSelect}
          hideFilters={["people"]}
          pillColor="purple"
          title="Access rules"
          showUserTypeOptions={true}
          hideUserTypes={[UserType.Admin]}
        />
      )}
    </>
  );
};

export default LibraryAccessAutomationDialog;
